import { HyggeLogo } from '@/components/icons/hygge-logo';
import { useAuthStore } from '@/stores/auth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LogOut, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useIsSuperAdmin } from '@/lib/permissions';

interface PlaceholderScreenProps {
  name: string;
  avatarUrl?: string | null;
}

export function PlaceholderScreen({ name, avatarUrl }: PlaceholderScreenProps) {
  const logout = useAuthStore((s) => s.logout);
  const isSuperAdmin = useIsSuperAdmin();

  const initials = name
    .split(/\s+/)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-8">
      {/* Modal card */}
      <div
        className={cn(
          'w-full max-w-[420px] rounded-2xl border border-border',
          'bg-card shadow-2xl dark:shadow-black/40',
          'flex flex-col items-center text-center',
          'px-8 pt-10 pb-8 gap-6'
        )}
      >
        {/* Avatar with ring */}
        <div className="relative">
          <Avatar className="h-16 w-16 rounded-full ring-2 ring-hygge/30 ring-offset-2 ring-offset-card">
            <AvatarImage src={avatarUrl ?? undefined} alt={name} />
            <AvatarFallback className="text-lg font-semibold bg-hygge/20 text-hygge">
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>

        {/* Logo */}
        <HyggeLogo className="h-9 w-auto text-foreground" />

        {/* Status badge */}
        <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Account linked successfully
        </div>

        {/* Greeting */}
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Welcome back</p>
          <h1
            className={cn(
              'text-2xl font-bold tracking-tight',
              'bg-gradient-to-r from-foreground via-foreground to-hygge bg-clip-text text-transparent'
            )}
          >
            Hey, {name}!
          </h1>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground leading-relaxed max-w-[320px]">
          We're building something great. Your workspace will be ready very soon.
        </p>

        {/* Setup progress */}
        <div className="w-full space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium uppercase tracking-wider text-muted-foreground">
              Setup progress
            </span>
            <span className="font-semibold text-foreground">78%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-gradient-to-r from-hygge to-hygge/80 transition-all"
              style={{ width: '78%' }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-2">
          {isSuperAdmin && (
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                window.location.href = '/settings/roles';
              }}
            >
              <Settings className="h-4 w-4 mr-2" />
              Manage Settings
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="border-border/80 text-muted-foreground hover:text-foreground hover:bg-muted/50"
            onClick={async () => {
              await logout();
              window.location.href = '/login';
            }}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
